/**
 * Evidence utilities for claims-filing review.
 *
 * Central helpers to determine whether a requirement/value has evidence
 * attached via an artifact id or non-empty list of citation ids.
 */
import type { DataRequirement } from '@/services/types/data-requirements-types';

/**
 * Returns true when the provided context includes an `artifact_id` or a
 * non-empty array of `citation_ids`.
 */
export const hasEvidenceContext = (
  context: Record<string, unknown> | undefined,
): boolean => {
  if (!context) return false;
  const hasArtifact = Boolean(
    (context as { artifact_id?: unknown }).artifact_id,
  );
  const citationIds = (context as { citation_ids?: unknown }).citation_ids as
    | string[]
    | undefined;
  const hasCitations = Array.isArray(citationIds) && citationIds.length > 0;
  return hasArtifact || hasCitations;
};

/**
 * Returns true if any value in the requirement contains evidence in its
 * context.
 */
export const fieldHasAnyEvidence = (
  req: DataRequirement | undefined,
): boolean => {
  if (!req) return false;
  const allValues = (req.values || []) as Array<{
    context?: Record<string, unknown>;
  }>;
  return allValues.some((v) => hasEvidenceContext(v?.context));
};
